<template>
  <BasePageTemplate :title="loginTitleString" :description="loginDescriptionString" v-model:error="error">
    <template #bottom>
      <Button class="p-0 bg-transparent" :label="notYouString" link :disabled="disabled" @click="onBackClick"></Button>
    </template>

    <DynamicForm v-model:data="data" :definition="formDefinition" :form-id="formId">
      <!-- Password field -->
      <Field :label="passwordString" is-required target="password">
        <PasswordInput :ref="passwordInputId" v-model="data.password" :disabled="disabled" @enter="onSignInClick" />
      </Field>

      <!-- Other options -->
      <div v-if="allowRememberMe" class="flex items-center gap-2">
        <Checkbox
          :modelValue="data.rememberMe"
          binary
          :disabled="disabled"
          :pt="{
            input: {
              id: rememberMeInputId,
            },
          }"
        />

        <label :for="rememberMeInputId" class="white-space-nowrap cursor-pointer" @click="onRememberMeClick">
          {{ rememberMeString }}
        </label>
      </div>

      <MainButton :label="signInString" :disabled="disabled" :loading="disabled && !isLoginWithPasskey" @click="onSignInClick" />

      <Button
        v-if="signInPreferences.passkeyEnabled"
        :label="signInWithPasskeyString"
        :disabled="disabled || isLoginWithPasskey"
        :loading="isLoginWithPasskey"
        @click="onSignInWithPasskeyClickAsync"
      ></Button>
    </DynamicForm>

    <!-- Other buttons -->
    <div v-if="allowForgotPassword" class="flex items-center justify-center">
      <Button class="p-0" :label="forgotPasswordString" link :disabled="disabled" @click="onForgotPasswordClickAsync"></Button>
    </div>

    <div class="flex items-center justify-center">
      <Button
        icon="chevron_left"
        rounded
        outlined
        :disabled="disabled"
        :aria-label="backString"
        v-tooltip.top="backString"
        @click="onBackClick"
      ></Button>
    </div>
  </BasePageTemplate>
</template>

<script setup lang="ts">
import { useGlobalConfigStore } from "@/features/Shared/stores";
import { AuthHttpHeader, Endpoints } from "@authentication/constants";
import { useRedirectionTools } from "@authentication/hooks";
import { AuthenticationApiService } from "@authentication/services";
import type { SignInPreferencesType, SignInType } from "@authentication/types";
// biome-ignore lint/style/useImportType:
import { BasePageTemplate, PasswordInput } from "@authentication/ui";
import type { IFormDefinition } from "@common/core";
import { useForm, useProblemDetailsUtils } from "@common/hooks";
import type { ProblemDetailsType } from "@common/types";
import { Button, Checkbox, DynamicForm, Field, MainButton } from "@common/ui";
import { client } from "@passwordless-id/webauthn";
import type { AuthenticationJSON } from "@passwordless-id/webauthn/dist/esm/types";
import { HttpStatusCode } from "axios";
import { useTranslation } from "i18next-vue";
import { nextTick, onMounted, ref, useId, useTemplateRef } from "vue";
import { useRoute } from "vue-router";
import * as yup from "yup";

// Models
const data = defineModel<SignInType>("data", { required: true });
const signInPreferences = defineModel<SignInPreferencesType>("preferences", { required: true });

// Emits
const emit = defineEmits<{
  (e: "back"): void;
  (e: "next", redirectTo: string): void;
}>();

// Constants
const formId = useId();
const passwordInputId = useId();
const rememberMeInputId = useId();

// Variables
const { t } = useTranslation();
const route = useRoute();
const { allowRememberMe, allowForgotPassword, isRaw } = useGlobalConfigStore();
const problemDetailUtils = useProblemDetailsUtils();
const redirectionTools = useRedirectionTools();
const dataFrom = useForm([formId]);
const passwordInput = useTemplateRef<InstanceType<typeof PasswordInput>>(passwordInputId);
const disabled = ref(false);
const isLoginWithPasskey = ref(false);
const error = ref<ProblemDetailsType | null>();

// Strings
const backString = t("commons.back");
const badRequestString = t("login.bad_request");
const forgotPasswordString = t("login.forgot_password");
const loginTitleString = t("login.form_title");
const lockedErrorString = t("login.locked");
const loginDescriptionString = t("login.form_description_username", { username: data.value.username });
const notYouString = t("login.not_you");
const passwordString = t("identity_server_application.password");
const rememberMeString = t("login.remember_me");
const signInString = t("login.sing_in");
const signInErrorString = t("login.sing_in_error");
const signInWithPasskeyString = t("login.sing_in_with_passkey");
const signInWithPasskeyErrorString = t("login.sign_in_with_passkey_error");
const usernameString = t("identity_server_application.username");

// Forms
const formDefinition = {
  username: {
    spec: yup.string().required().min(3).label(usernameString),
  },
  password: {
    spec: yup.string().required().min(3).label(passwordString),
  },
} as IFormDefinition;

// Methods
const onBackClick = () => {
  emit("back");
};

const onRememberMeClick = () => {
  if (disabled.value) return;

  data.value.rememberMe = !data.value.rememberMe;
};

const onSignInClick = async () => {
  disabled.value = true;
  error.value = null;

  dataFrom.cleanErrors();

  if (!(await dataFrom.isValidAsync())) {
    disabled.value = false;
    return;
  }

  AuthenticationApiService.signInAsync(data.value)
    .then((response) => {
      if (isRaw) {
        (window.parent as any).dashaCloseIdleLoginDlg();
      } else {
        const redirectUri = response.headers["x-location"] ?? route.query.returnurl ?? route.query.redirect_uri;

        emit("next", redirectUri);
      }
    })
    .catch((e: ProblemDetailsType) => {
      if (e.status === HttpStatusCode.Locked) {
        setErrorMessage(lockedErrorString);
      } else if (e.status === HttpStatusCode.Forbidden) {
        setErrorMessage(signInErrorString);
      } else {
        setErrorMessage(badRequestString);
      }

      disabled.value = false;
    });
};

const onSignInWithPasskeyClickAsync = async () => {
  let challenge = "";
  let response: AuthenticationJSON;

  disabled.value = true;
  isLoginWithPasskey.value = true;
  error.value = null;

  try {
    challenge = await AuthenticationApiService.initSignInWithPasskeyAsync();
  } catch (e) {
    error.value = e as ProblemDetailsType;
    disabled.value = false;
    isLoginWithPasskey.value = false;
    return;
  }

  try {
    response = await client.authenticate({
      challenge: challenge,
      userVerification: "required",
      timeout: 6000,
    });
  } catch {
    disabled.value = false;
    isLoginWithPasskey.value = false;
    return;
  }

  AuthenticationApiService.signInWithPasskeyAsync(response)
    .then((response) => {
      const redirectUri = response.headers[AuthHttpHeader.XLocation];

      emit("next", redirectUri);
    })
    .catch((_) => {
      setErrorMessage(signInWithPasskeyErrorString);
      disabled.value = false;
      isLoginWithPasskey.value = false;
    });
};

const onForgotPasswordClickAsync = async () => {
  await redirectionTools.redirectToAsync(Endpoints.pageResetCredentialsPath);
};

const setErrorMessage = (message: string) => {
  error.value = problemDetailUtils.fromString(message);
};

// Hooks
onMounted(async () => {
  await nextTick(() => {
    passwordInput.value?.focus();
  });
});
</script>
